import { DebugLogger } from '@affine/debug';
import { Unreachable } from '@affine/env/constant';
import type { DocMode } from '@blocksuite/affine/model';
import { replaceIdMiddleware } from '@blocksuite/affine/shared/adapters';
import type { AffineTextAttributes } from '@blocksuite/affine/shared/types';
import type { DeltaInsert, SliceSnapshot } from '@blocksuite/affine/store';
import { Slice, Text, Transformer } from '@blocksuite/affine/store';
import { ObjectPool, Service } from '@toeverything/infra';
import { combineLatest, map } from 'rxjs';

import { initDocFromProps } from '../../../blocksuite/initialization';
import type { DocProperties } from '../../db';
import { getAFFiNEWorkspaceSchema } from '../../workspace/global-schema';
import type { Doc } from '../entities/doc';
import { DocRecordList } from '../entities/record-list';
import { DocCreated, DocInitialized } from '../events';
import type { DocCreateMiddleware } from '../providers/doc-create-middleware';
import { DocScope } from '../scopes/doc';
import type { DocPropertiesStore } from '../stores/doc-properties';
import type { DocsStore } from '../stores/docs';
import type { DocCreateOptions } from '../types';
import { DocService } from './doc';
import { getDuplicatedDocTitle } from './duplicate-title';

const logger = new DebugLogger('DocsService');

export class DocsService extends Service {
  list = this.framework.createEntity(DocRecordList);

  pool = new ObjectPool<string, Doc>({
    onDelete(obj) {
      obj.scope.dispose();
    },
  });

  /**
   * Get all property values of a property, used for search
   *
   * Results may include docs in trash or deleted docs
   * Legacy property data such as old `journal` will not be included in the values
   */
  propertyValues$(propertyKey: string) {
    return combineLatest([
      this.store.watchDocIds(),
      this.docPropertiesStore.watchPropertyAllValues(propertyKey),
    ]).pipe(
      map(([docIds, propertyValues]) => {
        const result = new Map<string, string | undefined>();
        for (const docId of docIds) {
          result.set(docId, propertyValues.get(docId));
        }
        return result;
      })
    );
  }

  /**
   * used for search
   */
  allDocsCreatedDate$() {
    return this.store.watchAllDocCreateDate();
  }

  /**
   * used for search
   */
  allDocsUpdatedDate$() {
    return this.store.watchAllDocUpdatedDate();
  }

  allDocsTagIds$() {
    return this.store.watchAllDocTagIds();
  }

  allDocIds$() {
    return this.store.watchDocIds();
  }

  allNonTrashDocIds$() {
    return this.store.watchNonTrashDocIds();
  }

  allTrashDocIds$() {
    return this.store.watchTrashDocIds();
  }

  allDocTitle$() {
    return this.store.watchAllDocTitle();
  }

  constructor(
    private readonly store: DocsStore,
    private readonly docPropertiesStore: DocPropertiesStore,
    private readonly docCreateMiddlewares: DocCreateMiddleware[]
  ) {
    super();
  }

  loaded(docId: string) {
    const exists = this.pool.get(docId);
    if (exists) {
      return { doc: exists.obj, release: exists.release };
    }
    return null;
  }

  getBlocksuiteCollection() {
    return this.store.getBlocksuiteCollection();
  }

  open(docId: string) {
    const docRecord = this.list.doc$(docId).value;
    if (!docRecord) {
      throw new Error('Doc record not found');
    }
    const blockSuiteDoc = this.store.getBlockSuiteDoc(docId);
    if (!blockSuiteDoc) {
      throw new Error('Doc not found');
    }

    const exists = this.pool.get(docId);
    if (exists) {
      return { doc: exists.obj, release: exists.release };
    }

    const docScope = this.framework.createScope(DocScope, {
      docId,
      blockSuiteDoc,
      record: docRecord,
    });

    try {
      blockSuiteDoc.load();
    } catch (e) {
      logger.error('Failed to load doc', {
        docId,
        error: e,
      });
    }

    const doc = docScope.get(DocService).doc;

    doc.scope.emitEvent(DocInitialized, doc);

    const { obj, release } = this.pool.put(docId, doc);

    return { doc: obj, release };
  }

  createDoc(options: DocCreateOptions = {}) {
    for (const middleware of this.docCreateMiddlewares) {
      options = middleware.beforeCreate
        ? middleware.beforeCreate(options)
        : options;
    }
    const id = this.store.createDoc(options.id);
    const docStore = this.store.getBlockSuiteDoc(id);
    if (!docStore) {
      throw new Error('Failed to create doc');
    }
    if (options.skipInit !== true) {
      initDocFromProps(docStore, options.docProps, options);
    }
    const docRecord = this.list.doc$(id).value;
    if (!docRecord) {
      throw new Unreachable();
    }
    if (options.primaryMode) {
      docRecord.setPrimaryMode(options.primaryMode);
    }
    if (options.isTemplate) {
      docRecord.setProperty('isTemplate', true);
    }
    for (const middleware of this.docCreateMiddlewares) {
      middleware.afterCreate?.(docRecord, options);
    }
    docRecord.setCreatedAt(Date.now());
    docRecord.setUpdatedAt(Date.now());
    this.eventBus.emit(DocCreated, {
      doc: docRecord,
      docCreateOptions: options,
    });
    return docRecord;
  }

  async addLinkedDoc(targetDocId: string, linkedDocId: string) {
    const { doc, release } = this.open(targetDocId);
    const disposePriorityLoad = doc.addPriorityLoad(10);
    await doc.waitForSyncReady();
    disposePriorityLoad();
    const text = new Text([
      {
        insert: ' ',
        attributes: {
          reference: {
            type: 'LinkedPage',
            pageId: linkedDocId,
          },
        },
      },
    ] as DeltaInsert<AffineTextAttributes>[]);
    const [frame] = doc.blockSuiteDoc.getBlocksByFlavour('affine:note');
    frame &&
      doc.blockSuiteDoc.addBlock(
        'affine:paragraph' as never, // TODO(eyhn): fix type
        { text },
        frame.id
      );
    release();
  }

  async changeDocTitle(docId: string, newTitle: string) {
    const { doc, release } = this.open(docId);
    const disposePriorityLoad = doc.addPriorityLoad(10);
    await doc.waitForSyncReady();
    disposePriorityLoad();
    doc.changeDocTitle(newTitle);
    release();
  }

  async duplicate(sourceDocId: string, _targetDocId?: string) {
    const targetDocId = _targetDocId ?? this.createDoc().id;

    // check if source doc is removed
    if (this.list.doc$(sourceDocId).value?.trash$.value) {
      console.warn(
        `Template doc(id: ${sourceDocId}) is removed, skip duplicate`
      );
      return targetDocId;
    }

    const { release: sourceRelease, doc: sourceDoc } = this.open(sourceDocId);
    const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
    await sourceDoc.waitForSyncReady();

    // duplicate doc content
    try {
      const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
      const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
      if (!sourceBsDoc) throw new Error('Source doc not found');
      if (!targetBsDoc) throw new Error('Target doc not found');

      // clear the target doc (both surface and note)
      targetBsDoc.root?.children.forEach(child =>
        targetBsDoc.deleteBlock(child)
      );

      const collection = this.store.getBlocksuiteCollection();
      const transformer = new Transformer({
        schema: getAFFiNEWorkspaceSchema(),
        blobCRUD: collection.blobSync,
        docCRUD: {
          create: (id: string) => {
            this.createDoc({ id });
            const store = collection.getDoc(id)?.getStore({ id });
            if (!store) {
              throw new Error('Failed to create doc');
            }
            return store;
          },
          get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
          delete: (id: string) => collection.removeDoc(id),
        },
        middlewares: [replaceIdMiddleware(collection.idGenerator)],
      });
      const slice = Slice.fromModels(sourceBsDoc, [
        ...(sourceBsDoc.root?.children ?? []),
      ]);
      const snapshot = transformer.sliceToSnapshot(slice);
      if (!snapshot) {
        throw new Error('Failed to create snapshot');
      }
      await transformer.snapshotToSlice(
        snapshot,
        targetBsDoc,
        targetBsDoc.root?.id
      );
    } catch (e) {
      logger.error('Failed to duplicate doc', {
        sourceDocId,
        targetDocId,
        originalTargetDocId: _targetDocId,
        error: e,
      });
    } finally {
      sourceRelease();
      targetRelease();
    }

    // duplicate doc meta
    targetDoc.record.setMeta({
      tags: sourceDoc.meta$.value.tags,
    });

    // duplicate doc title
    targetDoc.changeDocTitle(getDuplicatedDocTitle(sourceDoc.title$.value));

    // duplicate doc properties
    const properties = sourceDoc.getProperties();
    const removedProperties = ['id', 'isTemplate', 'journal'];
    removedProperties.forEach(key => {
      delete properties[key];
    });
    targetDoc.updateProperties(properties);

    return targetDocId;
  }

  /**
   * Transformer wired to the current workspace collection. Shared by every
   * copy path (duplicate / template / snapshot) so the docCRUD wiring lives in
   * one place.
   */
  private createTransformer(
    middlewares: ConstructorParameters<
      typeof Transformer
    >[0]['middlewares'] = []
  ) {
    const collection = this.store.getBlocksuiteCollection();
    return new Transformer({
      schema: getAFFiNEWorkspaceSchema(),
      blobCRUD: collection.blobSync,
      docCRUD: {
        create: (id: string) => {
          this.createDoc({ id });
          const store = collection.getDoc(id)?.getStore({ id });
          if (!store) {
            throw new Error('Failed to create doc');
          }
          return store;
        },
        get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
        delete: (id: string) => collection.removeDoc(id),
      },
      middlewares,
    });
  }

  /**
   * Export a doc's content as a slice snapshot — the portable form used by the
   * account-level template library. Ids are left as-is; they are rewritten when
   * the snapshot is applied.
   */
  async createSliceSnapshotFromDoc(
    sourceDocId: string
  ): Promise<SliceSnapshot | null> {
    const { release, doc } = this.open(sourceDocId);
    const disposePriorityLoad = doc.addPriorityLoad(10);
    await doc.waitForSyncReady();
    disposePriorityLoad();
    try {
      const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
      if (!sourceBsDoc) throw new Error('Source doc not found');
      const transformer = this.createTransformer();
      const slice = Slice.fromModels(sourceBsDoc, [
        ...(sourceBsDoc.root?.children ?? []),
      ]);
      return transformer.sliceToSnapshot(slice) ?? null;
    } catch (e) {
      logger.error('Failed to create slice snapshot', {
        sourceDocId,
        error: e,
      });
      return null;
    } finally {
      release();
    }
  }

  /**
   * Apply a slice snapshot into a doc, replacing its content. Counterpart of
   * {@link createSliceSnapshotFromDoc} — used to instantiate account-level
   * templates, which have no live source doc in this workspace.
   *
   * @returns the id of the target doc
   */
  async duplicateFromSliceSnapshot(
    snapshot: SliceSnapshot,
    options: {
      targetDocId?: string;
      properties?: Partial<DocProperties>;
      primaryMode?: DocMode;
    } = {}
  ) {
    const targetDocId = options.targetDocId ?? this.createDoc().id;
    const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
    await targetDoc.waitForSyncReady();

    try {
      const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
      if (!targetBsDoc) throw new Error('Target doc not found');

      // clear the target doc (both surface and note)
      targetBsDoc.root?.children.forEach(child =>
        targetBsDoc.deleteBlock(child)
      );

      const collection = this.store.getBlocksuiteCollection();
      // mandatory: without id replacement `snapshotToSlice` would move the
      // existing blocks instead of inserting copies
      const transformer = this.createTransformer([
        replaceIdMiddleware(collection.idGenerator),
      ]);
      await transformer.snapshotToSlice(
        snapshot,
        targetBsDoc,
        targetBsDoc.root?.id
      );
    } catch (e) {
      logger.error('Failed to duplicate doc from snapshot', {
        targetDocId,
        error: e,
      });
    } finally {
      targetRelease();
    }

    // the title is deliberately left alone, mirroring `duplicateFromTemplate`
    if (options.properties) {
      const { doc, release } = this.open(targetDocId);
      doc.updateProperties(options.properties);
      release();
    }
    if (options.primaryMode) {
      this.list.doc$(targetDocId).value?.setPrimaryMode(options.primaryMode);
    }

    return targetDocId;
  }

  /**
   * Duplicate a doc from template
   * @param sourceDocId - the id of the source doc to be duplicated
   * @param _targetDocId - the id of the target doc to be duplicated, if not provided, a new doc will be created
   * @returns the id of the new doc
   */
  async duplicateFromTemplate(sourceDocId: string, _targetDocId?: string) {
    const targetDocId = _targetDocId ?? this.createDoc().id;

    // check if source doc is removed
    if (this.list.doc$(sourceDocId).value?.trash$.value) {
      console.warn(
        `Template doc(id: ${sourceDocId}) is removed, skip duplicate`
      );
      return targetDocId;
    }

    const { release: sourceRelease, doc: sourceDoc } = this.open(sourceDocId);
    const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
    await sourceDoc.waitForSyncReady();

    // duplicate doc content
    try {
      const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
      const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
      if (!sourceBsDoc) throw new Error('Source doc not found');
      if (!targetBsDoc) throw new Error('Target doc not found');

      // clear the target doc (both surface and note)
      targetBsDoc.root?.children.forEach(child =>
        targetBsDoc.deleteBlock(child)
      );

      const collection = this.store.getBlocksuiteCollection();
      const transformer = new Transformer({
        schema: getAFFiNEWorkspaceSchema(),
        blobCRUD: collection.blobSync,
        docCRUD: {
          create: (id: string) => {
            this.createDoc({ id });
            const store = collection.getDoc(id)?.getStore({ id });
            if (!store) {
              throw new Error('Failed to create doc');
            }
            return store;
          },
          get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
          delete: (id: string) => collection.removeDoc(id),
        },
        middlewares: [replaceIdMiddleware(collection.idGenerator)],
      });
      const slice = Slice.fromModels(sourceBsDoc, [
        ...(sourceBsDoc.root?.children ?? []),
      ]);
      const snapshot = transformer.sliceToSnapshot(slice);
      if (!snapshot) {
        throw new Error('Failed to create snapshot');
      }
      await transformer.snapshotToSlice(
        snapshot,
        targetBsDoc,
        targetBsDoc.root?.id
      );
    } catch (e) {
      logger.error('Failed to duplicate doc', {
        sourceDocId,
        targetDocId,
        originalTargetDocId: _targetDocId,
        error: e,
      });
    } finally {
      sourceRelease();
      targetRelease();
    }

    // duplicate doc properties
    const properties = sourceDoc.getProperties();
    const removedProperties = ['id', 'isTemplate', 'journal'];
    removedProperties.forEach(key => {
      delete properties[key];
    });
    targetDoc.updateProperties(properties);

    return targetDocId;
  }

  /**
   * Duplicate multiple docs in a single transformer session.
   *
   * All target docs are pre-created and their `sourceId -> targetId` mapping
   * is seeded into the id-replacement middleware, so cross-doc references
   * between the duplicated docs (inline links, embed linked/synced docs,
   * database relations) are rewritten to the new copies. References to docs
   * outside `sourceDocIds` keep pointing to the originals.
   *
   * Trashed source docs are skipped. Titles are preserved as-is.
   *
   * @param sourceDocIds - the ids of the source docs to be duplicated
   * @returns a map of `sourceDocId -> targetDocId`
   */
  async duplicateDocsBatch(
    sourceDocIds: string[]
  ): Promise<Map<string, string>> {
    const docIdMap = new Map<string, string>();
    const collection = this.store.getBlocksuiteCollection();

    // 1. pre-create all target docs and build the id map (used to seed the
    //    middleware before any content is imported)
    for (const sourceDocId of sourceDocIds) {
      if (this.list.doc$(sourceDocId).value?.trash$.value) {
        continue; // skip trashed docs
      }
      if (docIdMap.has(sourceDocId)) {
        continue;
      }
      const targetDocId = this.createDoc().id;
      docIdMap.set(sourceDocId, targetDocId);
    }

    if (docIdMap.size === 0) {
      return docIdMap;
    }

    // 2. single transformer, seeded so refs across these docs resolve to copies
    const transformer = new Transformer({
      schema: getAFFiNEWorkspaceSchema(),
      blobCRUD: collection.blobSync,
      docCRUD: {
        create: (id: string) => {
          this.createDoc({ id });
          const store = collection.getDoc(id)?.getStore({ id });
          if (!store) {
            throw new Error('Failed to create doc');
          }
          return store;
        },
        get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
        delete: (id: string) => collection.removeDoc(id),
      },
      middlewares: [replaceIdMiddleware(collection.idGenerator, docIdMap)],
    });

    // 3. copy content + properties for each doc
    for (const [sourceDocId, targetDocId] of docIdMap) {
      const { release: sourceRelease, doc: sourceDoc } = this.open(sourceDocId);
      const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
      try {
        await sourceDoc.waitForSyncReady();
        const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
        const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
        if (!sourceBsDoc) throw new Error('Source doc not found');
        if (!targetBsDoc) throw new Error('Target doc not found');

        // clear the freshly created target doc (both surface and note)
        targetBsDoc.root?.children.forEach(child =>
          targetBsDoc.deleteBlock(child)
        );

        const slice = Slice.fromModels(sourceBsDoc, [
          ...(sourceBsDoc.root?.children ?? []),
        ]);
        const snapshot = transformer.sliceToSnapshot(slice);
        if (!snapshot) {
          throw new Error('Failed to create snapshot');
        }
        await transformer.snapshotToSlice(
          snapshot,
          targetBsDoc,
          targetBsDoc.root?.id
        );

        // duplicate doc meta + title (keep original title for folder templates)
        targetDoc.record.setMeta({
          tags: sourceDoc.meta$.value.tags,
        });
        targetDoc.changeDocTitle(sourceDoc.title$.value);

        // duplicate doc properties
        const properties = sourceDoc.getProperties();
        const removedProperties = ['id', 'isTemplate', 'journal'];
        removedProperties.forEach(key => {
          delete properties[key];
        });
        targetDoc.updateProperties(properties);
      } catch (e) {
        logger.error('Failed to duplicate doc in batch', {
          sourceDocId,
          targetDocId,
          error: e,
        });
      } finally {
        sourceRelease();
        targetRelease();
      }
    }

    return docIdMap;
  }
}
